import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router';
import {
  BarChart3,
  CalendarDays,
  ChevronsLeft,
  ChevronsRight,
  CalendarCheck,
  Inbox,
  KanbanSquare,
  ListChecks,
  ListTodo,
  MessageSquare,
  NotebookPen,
  Plus,
  Server,
  Settings,
  Sun,
  Timer,
} from 'lucide-react';
import { PERMISSIONS, type Permission } from '@virtuetasks/types';
import { useTodoBadge } from '@/features/todos/api';
import { useChatUnread } from '@/features/chat/api';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth, useCan } from '@/features/auth';
import { useUiStore } from '@/stores/ui';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  permission?: Permission;
  /**
   * V5-01 §6 — some surfaces are closed to guests outright, not by grant.
   * Chat is one: a guest holds `task.view` for their shared project, so a
   * permission gate would have let them straight in.
   */
  notForGuests?: boolean;
}

/**
 * Navigation is permission-gated — §3.4.
 *
 * Not decoration: a guest holds `none` on leave, time, notes and reports
 * (§3.1), and showing them a Leave tab that 403s is both a worse product and a
 * hint about surfaces they should not know exist. Hiding is UX; the server
 * re-checks regardless.
 */
const PRIMARY_NAV: NavItem[] = [
  { to: '/my-day', label: 'My Day', icon: Sun, permission: PERMISSIONS.TASK_VIEW },
  // V5-04: private to-dos. No permission gate — everyone has their own, and
  // there is nothing here that belongs to the workspace.
  { to: '/todos', label: 'My To-dos', icon: ListChecks },
  // V5-01 §5, §6 — guests never see chat, so this is permission-gated on a
  // grant they do not hold rather than hidden by a role check in the row.
  { to: '/chat', label: 'Chat', icon: MessageSquare, notForGuests: true },
  { to: '/inbox', label: 'Inbox', icon: Inbox, permission: PERMISSIONS.TASK_VIEW },
  { to: '/my-tasks', label: 'My Tasks', icon: ListTodo, permission: PERMISSIONS.TASK_VIEW },
  { to: '/notes', label: 'Notes', icon: NotebookPen, permission: PERMISSIONS.NOTE_CREATE },
  { to: '/timesheet', label: 'Timesheet', icon: Timer, permission: PERMISSIONS.TIME_LOG },
  { to: '/leave', label: 'Leave', icon: CalendarDays, permission: PERMISSIONS.LEAVE_REQUEST },
  // V6-04 §4 — everyone has their own attendance; guests have none at all.
  { to: '/attendance', label: 'Attendance', icon: CalendarCheck, notForGuests: true },
];

const FOOTER_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: BarChart3, permission: PERMISSIONS.REPORT_VIEW_OWN },
  { to: '/settings/profile', label: 'Settings', icon: Settings },
];

/**
 * The platform console link — V3 R5.
 *
 * Shown by probing the API rather than reading a claim: `platformRole` is
 * deliberately absent from the token and from `/auth/me`, because no client
 * should be able to grant itself the link by editing state. A 403 simply means
 * the row never renders.
 */
function usePlatformAccess(): boolean {
  const { data } = useQuery({
    queryKey: ['platform-access'],
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      await api.get('/platform/metrics');
      return true;
    },
  });
  return data === true;
}

function SectionLabel({
  children,
  collapsed,
  action,
}: {
  children: React.ReactNode;
  collapsed: boolean;
  action?: React.ReactNode;
}) {
  if (collapsed) return <div className="mx-2 my-2 h-px bg-[var(--border)]" />;
  return (
    <div className="flex items-center gap-1 px-3 pb-1 pt-4 text-[11px] font-medium uppercase tracking-wide text-[var(--text-muted)]">
      <span className="flex-1">{children}</span>
      {action}
    </div>
  );
}

function NavRow({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'mx-2 flex items-center gap-2.5 rounded-[6px] px-2 py-1.5 text-[13px] transition-colors duration-[120ms]',
          collapsed && 'justify-center px-0',
          isActive
            ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
            : 'text-[var(--text)] hover:bg-[var(--surface-2)]',
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && item.badge ? (
        <span className="ml-auto rounded-full bg-[var(--surface-2)] px-1.5 text-[11px] text-[var(--text-muted)]">
          {item.badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export function Sidebar({ projects }: { projects?: { key: string; name: string; icon: string }[] }) {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const setMobileNavOpen = useUiStore((s) => s.setMobileNavOpen);
  const location = useLocation();
  const toggle = useUiStore((s) => s.toggleSidebar);
  const openCreateProject = useUiStore((s) => s.setCreateProjectOpen);
  const { user } = useAuth();
  const can = useCan();
  const isPlatformAdmin = usePlatformAccess();
  const { data: todoBadge } = useTodoBadge();
  // Guests get no chat at all (§6), and asking for their badge would only 403.
  const { data: chatUnread } = useChatUnread(user?.systemRole !== 'guest');

  /**
   * V8-03 §2 — close the drawer when the route changes.
   *
   * Tapping a destination and being left looking at the menu you tapped it in
   * is the single most common way a mobile drawer feels broken.
   */
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, setMobileNavOpen]);

  return (
    <>
      {/**
       * The backdrop, below `md` only. It is what makes "tap anywhere to
       * dismiss" work, and it is also what stops a tap landing on the page
       * underneath the drawer.
       */}
      {mobileNavOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          data-testid="mobile-nav-backdrop"
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

    <aside
      id="primary-navigation"
      data-testid="primary-navigation"
      data-mobile-open={mobileNavOpen ? 'true' : 'false'}
      className={cn(
        'shrink-0 flex-col border-r border-[var(--border)] bg-[var(--surface)] transition-[width] duration-[180ms] ease-out md:flex',
        collapsed ? 'md:w-[56px]' : 'md:w-[240px]',
        /**
         * V8-03 §2 — below `md` this is a drawer, not a column.
         *
         * It used to be `hidden … md:flex`: `display:none` on a phone, full
         * stop. The hamburger toggled the desktop collapse width of an element
         * nobody could see, which is the whole of "tapping the menu does
         * nothing". On mobile it is now fixed, full-height and 260px wide,
         * sliding in over the page.
         */
        mobileNavOpen
          ? 'fixed inset-y-0 left-0 z-40 flex w-[260px] shadow-xl md:static md:z-auto md:w-auto md:shadow-none'
          : 'hidden md:flex',
      )}
      aria-label="Primary"
    >
      <nav className="flex-1 overflow-y-auto py-2">
        {PRIMARY_NAV.filter(
          (item) =>
            (!item.permission || can(item.permission)) &&
            !(item.notForGuests && user?.systemRole === 'guest'),
        ).map((item) => (
          <NavRow
            key={item.to}
            // V5-04: the to-do badge counts what is due today or already
            // overdue, so it means "look at this", not "you have some".
            item={
              item.to === '/todos'
                ? { ...item, badge: todoBadge?.dueOrOverdue }
                : item.to === '/chat'
                  ? { ...item, badge: chatUnread?.total }
                  : item
            }
            collapsed={collapsed}
          />
        ))}

        {/* V3 B2: project creation had no affordance outside /projects, so
            nobody found it. The "+" opens the same dialog from anywhere. */}
        <SectionLabel
          collapsed={collapsed}
          action={
            can(PERMISSIONS.PROJECT_CREATE) ? (
              <button
                type="button"
                onClick={() => openCreateProject(true)}
                aria-label="New project"
                title="New project"
                data-testid="sidebar-new-project"
                className="grid size-5 place-items-center rounded-[4px] text-[var(--text-muted)] transition-colors duration-[120ms] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
              >
                <Plus className="size-3.5" />
              </button>
            ) : undefined
          }
        >
          Projects
        </SectionLabel>

        {/* V6-03 — above the project list, because it is the view *of* the
            list. Hidden when there is nothing in it: a guest shared into no
            project would open an empty board and learn nothing. */}
        {(projects ?? []).length > 0 && (
          <NavRow
            item={{ to: '/board', label: 'All Projects', icon: KanbanSquare }}
            collapsed={collapsed}
          />
        )}

        {(projects ?? []).map((project) => (
          <NavLink
            key={project.key}
            to={`/projects/${project.key}/board`}
            title={collapsed ? project.name : undefined}
            className={({ isActive }) =>
              cn(
                'mx-2 flex items-center gap-2.5 rounded-[6px] px-2 py-1.5 text-[13px] transition-colors duration-[120ms]',
                collapsed && 'justify-center px-0',
                isActive
                  ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent)]'
                  : 'text-[var(--text)] hover:bg-[var(--surface-2)]',
              )
            }
          >
            <span className="w-4 shrink-0 text-center leading-none">{project.icon}</span>
            {!collapsed && <span className="truncate">{project.name}</span>}
          </NavLink>
        ))}
        {!collapsed && (projects ?? []).length === 0 && (
          <div className="px-3 py-1">
            <p className="text-[12px] text-[var(--text-muted)]">No projects yet</p>
            {can(PERMISSIONS.PROJECT_CREATE) && (
              <button
                type="button"
                onClick={() => openCreateProject(true)}
                className="mt-1 text-[12px] font-medium text-[var(--accent)] hover:underline"
              >
                Create one
              </button>
            )}
          </div>
        )}

        <SectionLabel collapsed={collapsed}>&nbsp;</SectionLabel>
        {FOOTER_NAV.filter((item) => !item.permission || can(item.permission)).map((item) => (
          <NavRow key={item.to} item={item} collapsed={collapsed} />
        ))}
        {isPlatformAdmin && (
          <NavRow
            item={{ to: '/platform', label: 'Platform', icon: Server }}
            collapsed={collapsed}
          />
        )}
      </nav>

      <button
        type="button"
        onClick={toggle}
        className="m-2 flex items-center justify-center gap-2 rounded-[6px] px-2 py-1.5 text-[12px] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-2)]"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
        {!collapsed && <span>Collapse</span>}
      </button>
    </aside>
    </>
  );
}
